import { h } from 'preact'
import { useEffect, useState } from 'preact/compat'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { omnioAuthentication } from '../../../api/omnio'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import {
  ERROR_RESPONSES,
  SUCCESS_RESPONSES,
} from '../../../constants/serverResponses'
import { DataDog } from '../../../datadog'
import { APEXX_RESPONSE_ERROR_TEXT } from '../../../helpers/apexxTextResponses'
import { formatAsCurrency, redirect, sprintf } from '../../../helpers/common'
import {
  dispatchMFEAdobeEvent,
  dispatchMFEEvent,
  dispatchPaymentDetailsIframeEvent,
} from '../../../helpers/events'
import { OMNIO_ERROR_TEXT } from '../../../helpers/omnioBalanceErrors'
import { RESPONSE_ERROR_TEXT } from '../../../helpers/serverTextResponses'
import useAuthoriseError from '../../../hooks/useAuthoriseError'
import useFailureRedirect from '../../../hooks/useFailureRedirect'
import useTouchedFields from '../../../hooks/useTouchedFields'
import { useTranslations } from '../../../hooks/useTranslations'
import { billingAddressSelector } from '../../../redux/slice/billingAddressSlice'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { prePaymentSelector } from '../../../redux/slice/prePaymentSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'
import { selectIsPciFormEnabled } from '../../../redux/selectors/evervault'
import ErrorMessage from '../../Common/ErrorMessage'
import InfoMessage from '../../Common/InfoMessage'
import LinkButton from '../../Common/LinkButton'
import Loader from '../../Common/Loader'
import CardErrors from '../Card/CardErrors'

const OmnioCard = ({
  handleSubmit,
  showPaymentConfirmation,
  isGoBackButtonVisible,
  loaderImage,
  errors,
  iframeRef,
  watch,
}) => {
  const l10n = useTranslations()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const isPciFormEnabled = useSelector(selectIsPciFormEnabled)
  const pageState = useSelector(pageStateSelector)
  const [paymentBlockedError, setPaymentBlockedError] = useState('')
  const [isPaymentPending, setIsPaymentPending] = useState(false)
  const prePayment = useSelector(prePaymentSelector)
  const [formErrors, setFormErrors] = useState({})
  const billingAddress = useSelector(billingAddressSelector)
  const [iframeHasBeenTouchedAfterSubmit, setIframeHasBeenTouchedAfterSubmit] =
    useState(false)

  const [paymentErrorCode, setPaymentErrorCode] = useState(null)

  const { hasBeenTouchedAfterSubmit, setSubmittedData, setTouchedAfterSubmit } =
    useTouchedFields(watch, isPciFormEnabled)

  const formatAmount = formatAsCurrency(
    transactionDetails.currency,
    transactionDetails.culture,
  )

  const currentAmount = formatAmount(
    transactionDetails?.overrideAmount
      ? transactionDetails.overrideAmount
      : transactionDetails.amount,
  )

  const checkIsTermsAndConditionsEnabled = () => {
    if (pageState.isPaymentBlocked) {
      setPaymentBlockedError(l10n.t('payment-btn-error'))
      return false
    }
    setPaymentBlockedError('')

    return true
  }

  useEffect(() => {
    if (!pageState.isPaymentBlocked) {
      setPaymentBlockedError('')
    }
  }, [pageState.isPaymentBlocked])

  useFailureRedirect()
  const handleAuthoriseError = useAuthoriseError()

  const handleSubmission = data => {
    const { cardNumber, cardholderName, expirationMonth, expirationYear } =
      data

    DataDog.setCardIdentification(
      cardNumber,
      cardholderName,
      expirationMonth,
      expirationYear,
    )

    setSubmittedData(data)
    setPaymentErrorCode(null)

    const continuePayment = async () => {
      setIsPaymentPending(true)

      const { responseData, responseStatus } = await omnioAuthentication({
        cardNumber,
        CVC: data.cvv,
        expiryMonth: expirationMonth,
        expiryYear: `20${data.expirationYear}`,
        amount: transactionDetails?.overrideAmount
          ? transactionDetails.overrideAmount
          : transactionDetails.amount,
      })

      if (responseStatus === SUCCESS_RESPONSES.OK) {
        window.tuiPaymentsMFEInstance?.onAuthorised?.()
        setIsPaymentPending(false)
        setTouchedAfterSubmit(false)
        redirect(transactionDetails.successUrl)
        return
      }

      handleAuthoriseError(responseStatus, responseData)

      if (responseData?.isRetryable) {
        setPaymentErrorCode(responseData.errorCode)
      }

      setIsPaymentPending(false)
    }

    const mfeElement = window.tuiPaymentsMFEInstance

    if (mfeElement?.onAuthorising) {
      DataDog.info(`onAuthorising hook has been enabled`)

      mfeElement
        .onAuthorising?.({ paymentMethod: PAYMENT_METHOD_TYPES.Card })
        .then(() => {
          continuePayment()
          DataDog.info(`onAuthorising Promise resolved`)
        })
        .catch(() => {
          DataDog.info(`onAuthorising Promise rejected`)
          console.log('onAuthorising Promise rejected')
        })
    } else {
      continuePayment()
    }
  }

  const handlePayment = () => {
    DataDog.info('Omnio Card payment button clicked')
    dispatchMFEEvent('tui-button.payment-btn-clicked', {
      paymentMethod: 'Card',
    })
    dispatchMFEAdobeEvent('Pay-Card')

    if (isPciFormEnabled) {
      if (!pageState.isPaymentBlocked) {
        checkIsTermsAndConditionsEnabled()
      }

      dispatchPaymentDetailsIframeEvent(
        {
          type: 'PaymentsMFEOmnioPay',
          data: {
            isTermsAndConditionsEnabled: pageState.isPaymentBlocked,
          },
        },
        iframeRef?.current?.contentWindow,
      )

      return
    }

    handleSubmit(
      data => {
        if (!checkIsTermsAndConditionsEnabled()) {
          return
        }

        const { cardholderName } = data
        const cardNumber = data.number?.replace(/\s/g, '')
        const expirationMonth = `0${data.expirationMonth}`.slice(-2)
        const expirationYear = `0${data.expirationYear}`.slice(-2)

        handleSubmission({
          cardholderName,
          cardNumber,
          expirationMonth,
          expirationYear,
          cvv: data.cvv,
        })
      },
      () => checkIsTermsAndConditionsEnabled(),
    )()
  }

  const handlePCIIframeMessage = e => {
    const type = e?.data?.type

    if (
      type === 'MFEFormPropsUpdate' &&
      e.data.data?.hasBeenTouchedAfterSubmit
    ) {
      setIframeHasBeenTouchedAfterSubmit(e.data.data?.hasBeenTouchedAfterSubmit)
    } else if (type === 'PaymentsMFEOmnioFields') {
      handleSubmission(e.data.data)
    } else if (type === 'MFEFormPropsUpdate' && e.data.data?.errors) {
      setFormErrors(JSON.parse(e.data.data.errors))
    }
  }

  useEffect(() => {
    window.addEventListener('message', handlePCIIframeMessage)

    return () => window.removeEventListener('message', handlePCIIframeMessage)
  }, [pageState.isPaymentBlocked, billingAddress])

  useEffect(() => {
    if (prePayment.isRequestPending) {
      setPaymentErrorCode(null)
    }
  }, [prePayment.isRequestPending])

  const errorFields = isPciFormEnabled
    ? Object.keys(formErrors)
    : Object.keys(errors)

  return (
    <div className="payment-form">
      {showPaymentConfirmation && (
        <InfoMessage
          message={sprintf(l10n.t(`You're about to pay %s`), currentAmount)}
          data-testid="omnio-card-info-message"
          className="omnio-card-info-message"
        />
      )}

      <CardErrors
        hasBeenTouchedAfterSubmit={
          isPciFormEnabled
            ? iframeHasBeenTouchedAfterSubmit
            : hasBeenTouchedAfterSubmit
        }
        paymentBlockedError={paymentBlockedError}
        errorFields={errorFields}
        iframeRef={iframeRef}
        paymentErrorCode={paymentErrorCode}
      />

      {isPaymentPending ? (
        <Loader loaderImage={loaderImage || transactionDetails.loaderOption} />
      ) : (
        <div className="payment-form__payment-button">
          {isGoBackButtonVisible && (
            <LinkButton
              data-testid="go-back-button"
              title={l10n.t('Go Back')}
              onClick={() => redirect(transactionDetails.returnUrl)}
              className="go-back-button"
            />
          )}
          <input
            type="button"
            value={l10n.t('Pay Now')}
            className="button primary medium card-payment-btn"
            onClick={handlePayment}
            data-testid="omnio-payment-button"
            disabled={prePayment.hasError || prePayment.isRequestPending}
          />
        </div>
      )}
    </div>
  )
}

OmnioCard.propTypes = {
  handleSubmit: PropTypes.func,
  showPaymentConfirmation: PropTypes.bool,
  isGoBackButtonVisible: PropTypes.bool,
  loaderImage: PropTypes.string,
  errors: PropTypes.object,
  iframeRef: PropTypes.object,
  watch: PropTypes.func,
}

export default OmnioCard
